import { Link } from "react-router-dom";
import { Mail, MessageCircle, ShieldCheck, HelpCircle } from "lucide-react";
import { LogoBadge } from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO, breadcrumbLd, webPageLd } from "@/lib/seo";

const TITLE = "Contact";
const DESC =
  "Get in touch with the DoppelCrush team — feedback, safety reports, partnerships, press.";

const TOPICS = [
  {
    icon: HelpCircle,
    title: "Help or feedback",
    body: "Stuck somewhere, found a bug, or have an idea? We read every email.",
    email: "hello@doppelcrush.com",
  },
  {
    icon: ShieldCheck,
    title: "Safety report",
    body: "Saw something off — a fake selfie, harassment, or a user under 18? Tell us and we'll act fast.",
    email: "safety@doppelcrush.com",
  },
  {
    icon: MessageCircle,
    title: "Press & partnerships",
    body: "Writing about us or want to collaborate on a campus campaign? Reach out.",
    email: "press@doppelcrush.com",
  },
];

export default function Contact() {
  return (
    <div className="crush-bg min-h-screen" data-testid="contact-page">
      <SEO
        title={TITLE}
        description={DESC}
        path="/contact"
        jsonLd={[
          webPageLd({ title: TITLE, description: DESC, path: "/contact" }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10">
        <Link to="/"><LogoBadge /></Link>
        <div className="crush-frame mt-8 p-6 sm:p-10">
          <h1 className="font-display text-5xl font-bold leading-[0.95]">
            <span className="crush-text-grad">Contact</span> the team.
          </h1>
          <p className="mt-4 text-slate-700 font-body text-lg max-w-xl">
            We&apos;re a small team building DoppelCrush. The fastest way to reach us is email.
          </p>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {TOPICS.map((t) => (
              <a
                key={t.email}
                href={`mailto:${t.email}`}
                className="crush-frame p-5 hover:scale-[1.02] transition-transform"
                data-testid={`contact-${t.email.split("@")[0]}`}
              >
                <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-700 grid place-items-center">
                  <t.icon className="w-5 h-5" />
                </div>
                <div className="font-display font-bold text-slate-900 mt-3">{t.title}</div>
                <p className="text-xs font-body text-slate-600 mt-1">{t.body}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-pink-700 font-display font-bold text-sm">
                  <Mail className="w-3.5 h-3.5" /> {t.email}
                </div>
              </a>
            ))}
          </div>

          <div className="mt-10 text-sm font-body text-slate-600">
            See also <Link to="/faq" className="text-pink-600 underline">FAQ</Link>, <Link to="/safety" className="text-pink-600 underline">Safety</Link> and <Link to="/privacy" className="text-pink-600 underline">Privacy Policy</Link>.
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
